import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';

//Quill required imports
import Quill from 'quill';
import 'react-quill/dist/quill.snow.css';

//Constants
import { modulesForSendMessage } from '@constants/common/wysiwygEditor/modules';
import { BoldEdit, TextAlignContent, IndentStyle } from '@constants/common/wysiwygEditor/QuillClasses';
import { configs } from '@constants/configs/configs';
import { MAX_PDF_FILE_SIZE } from '@constants/ticketMgmt/ticketMgmt';
//Components
import { Form } from 'react-bootstrap';
import ErrorMessage from '@common-components/form/errorMessage';
import ReactQuill from 'react-quill';
import { FormattedMessage } from 'react-intl';
import ProgressBarContainer from '@containers/common/ProgressBarContainer';

//DOMPurify
import DOMPurify from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSelector } from 'react-redux';
import useCannedResponse from '../../../hooks/useCannedResponse';
import { CannedCategory } from '../../../constants/common/CannedCategory';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { KEYBOARD_KEYS_CODE_NAME } from '../../../constants/common/shortcutKeys';

//Component
const TicketSendMessageForm = props => {
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isInvalidFileError, setShowInvalidFileError] = useState(false);

    const activeSpinnersQty = useSelector(state => state.common.spinner.activeSpinnersQty);
    const isProcessing = useSelector(state => state.common.progress.processing);

    const trashIconClass = classNames({
        'cursor-not-allowed': isProcessing,
        'cursor-pointer': !isProcessing,
    });

    const uploadFileClass = classNames('styled-choose-file', {
        'cursor-not-allowed': isProcessing,
    });

    //constants in the component
    const spaceRegex = /^[^\s].*/;
    const minLengthRegex = /^.{6,}$/;
    const subjectLengthRegex = /^.{6,200}$/;

    //Updating quill component to have inline indent styling and bold fix for jsp pages
    Quill.register(IndentStyle(), true);
    Quill.register(BoldEdit(), true);
    Quill.register(TextAlignContent(), true);

    //props received from jsp page, environment and dispatch actions
    const {
        environment: { exchangeName },
        getConsumerInformation,
        postSendMessage,
        ticketId,
        encrTicketId,
        ticketObj,
    } = props;

    //initial state of the sendMessageForm.
    const [sendMessageState, setSendMessageState] = useState({
        data: {
            sendComment: false,
            commentTargetId: encrTicketId,
            subject: '',
            currentURL: `/${window.location.href.split('/hix/')[1]}`,
            ticketId: ticketId,
        },
        errorHandling: {
            subject: '',
            msgText: '',
            general: '',
        },
    });

    const { templateList = [], getCannedTemplatesList, errorMessage } = useCannedResponse();

    //This state is just to handle the text in progress in the editor
    //This state can't be nested in sendMessageState otherwise the library
    // doesn't know how to handle the setCurrentMessage
    const [currentMessage, setCurrentMessage] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [filteredItems, setFilteredItems] = useState([]);
    const [cursorIndex, setCursorIndex] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
    const [selectedSubject, setSelectedSubject] = useState('');
    const [showEditContentView, setShowEditContentView] = useState(false);
    const [editContent, setEditContent] = useState();
    const [templateObj, setTemplateObj] = useState();
    const [lastWordLength, setLastWordLength] = useState();
    const quillRef = useRef(null); // Create a ref for ReactQuill
    const inputTextAreaTemplateRef = useRef(null);
    const { ENTER, BACKSPACE, DELETE, ARROW_DOWN, ARROW_UP, ESCAPE } = KEYBOARD_KEYS_CODE_NAME;

    useEffect(() => {
        setShowSuggestions(filteredItems.length);
    }, [filteredItems]);

    useEffect(() => {
        validateFileSize();
    }, [uploadedFiles]);

    /*This is equivalent of componentDidMount(); I am making the intial API call here to get user's information.
        We send the ticket number which is stored in local storage from the JSP page that leads to this page and
        send it to the endpoint configs.endpoints.TICKET_MGMT_GET_CONSUMER_DATA.
        With this data we will fill out the user's information.
     */
    useEffect(() => {
        getCannedTemplatesList(CannedCategory.SEND_MESSAGE);
        getConsumerInformation(configs.endpoints.TICKET_MGMT_GET_CONSUMER_DATA, {
            ticketId: sendMessageState.data.commentTargetId,
        })
            .then(response => {
                const ticketDetails = response;
                if (ticketDetails) {
                    setSendMessageState(oldState => ({
                        ...oldState,
                        data: {
                            ...oldState.data,
                            addressBook: ticketDetails.toUserId,
                            subject: `${ticketDetails.num} ${ticketDetails.subject} needs your attention`,
                            names: ticketDetails.to,
                            commentType: 'TICKET_PUBLIC',
                            commentText: '',
                            body: '',
                            createFor: ticketDetails.createdFor,
                            ticketLink: ticketDetails.ticketLink,
                            ticketNum: ticketDetails.num,
                            createdForName: ticketDetails.createdForName,
                            ticFrom: ticketDetails.from,
                            requestor: ticketObj?.requestor,
                        },
                    }));
                }
            })
            .catch(() => {
                handleNestedChange('api_issues', 'errorHandling', 'general');
            });
    }, []);

    //This useEffect will determine when we make the API call
    useEffect(() => {
        if (sendMessageState.data.sendComment) {
            let body = document.createElement('div');
            body.innerHTML = document.getElementsByClassName('ql-editor')[0].innerHTML;
            let qlCurser = body.getElementsByClassName('ql-cursor');
            if (qlCurser.length) {
                qlCurser[0].parentNode.removeChild(qlCurser[0]);
            }
            //Base 64 encrypting the body so it won't get stripped when sent to the backend
            const clean = DOMPurify.sanitize(body.outerHTML);
            const bodyConverted = clean.replace(/[\u00A0-\u2666]/g, function (c) {
                return '&#' + c.charCodeAt(0) + ';';
            });
            const encodedBody = btoa(bodyConverted);

            setSendMessageState(oldState => ({
                ...oldState,
                data: {
                    ...oldState.data,
                    body: encodedBody,
                    isEncoded: true,
                    commentText: body.innerText,
                },
            }));
        }
    }, [sendMessageState.data.sendComment]);

    //Sends API call after body has been set
    useEffect(() => {
        const requestData = sendMessageState.data;
        const files = {};
        uploadedFiles &&
            uploadedFiles.forEach((file, index) => {
                files[`file${index}`] = file;
            });
        const requestBody = { ...requestData, ...files };
        if (sendMessageState.data.sendComment) {
            postSendMessage(configs.endpoints.SECURE_INBOX_SEND, requestBody).catch(() => {
                handleNestedChange('api_issues', 'errorHandling', 'general');
            });
        }
    }, [sendMessageState.data.body]);

    // Focus template text area
    useEffect(() => {
        if (showEditContentView && inputTextAreaTemplateRef.current) {
            const { current } = inputTextAreaTemplateRef;
            current.focus();
            const length = current.value.length;
            current.setSelectionRange(length, length);
        }
    }, [showEditContentView]);

    //This useEffect removesErrors when the data changes on select variables
    // if we have errors for them
    useEffect(() => {
        const { subject, msgText, general } = sendMessageState.errorHandling;
        if (subject || msgText || general) {
            setSendMessageState(oldState => ({
                ...oldState,
                errorHandling: {
                    subject: '',
                    msgText: '',
                    general: '',
                },
            }));
        }
    }, [sendMessageState.data.subject, currentMessage]);

    //Reusable setState to update the nested values in setSendMessageState.
    const handleNestedChange = (value, keyOne, keyTwo) => {
        setSendMessageState(oldState => ({
            ...oldState,
            [keyOne]: {
                ...oldState[keyOne],
                [keyTwo]: value,
            },
        }));
    };

    const validateFileSize = () => {
        const fileSize = uploadedFiles.reduce((acc, item) => acc + item.size, 0);
        const isError = fileSize > MAX_PDF_FILE_SIZE;
        setShowInvalidFileError(isError);
    };

    const extractFileName = pdfObject => {
        const fileName = pdfObject.name.split('.');
        fileName.pop();
        fileName.join(',', '').trim();
        return fileName.toString();
    };

    const getFileExtension = filename => {
        return filename.split('.').pop().toString().toUpperCase();
    };

    const onFileUploadHandler = event => {
        const filesArray = Object.entries(event.target.files).map(fileObj => fileObj[1]) || [];
        const fileExtension = filesArray.length > 0 ? filesArray.map(ext => getFileExtension(ext.name)) : null;
        const fileNameArray =
            uploadedFiles.length > 0 ? uploadedFiles.map(pdfObject => extractFileName(pdfObject)) : [];

        if (fileExtension && fileExtension.every(v => v.toUpperCase() === 'PDF')) {
            const newFileList = [];
            const isExistingFileFlag = uploadedFiles.length === 0;
            Object.entries(event.target.files).forEach(selectedPdfFileObject => {
                if (isExistingFileFlag) {
                    setUploadedFiles([...event.target.files]);
                    return;
                } else {
                    if (!fileNameArray.find(fileName => fileName === extractFileName(selectedPdfFileObject[1]))) {
                        newFileList.push(selectedPdfFileObject[1]);
                    }
                }
            });
            if (!isExistingFileFlag && newFileList.length > 0) {
                setUploadedFiles([...uploadedFiles, ...newFileList]);
                return;
            }
        } else {
            setShowInvalidFileError(true);
            return;
        }
    };

    const onDeleteFileHandler = idx => {
        if (isProcessing) {
            return;
        }
        setUploadedFiles(uploadedFiles.filter((file, index) => file && index !== idx));
    };

    //Reusable test to verify the string provided passes the regex test and that it exists
    const regexTester = (regex, string) => {
        return string && regex.test(string);
    };

    //Start of validation for the sendMessageform. This is where the errors will dynamically generate.
    const verificationForSendMessageForm = () => {
        //Checks if we have errors
        const hasErrors = Object.keys(sendMessageState.errorHandling).some(function (key) {
            return sendMessageState.errorHandling[key];
        });

        //We need to check if we have errors or if we are missing data. There is a error message that displays
        //when we are missing this data so we need a check for that since it is not in the
        //sendMessageState.errorHandling
        if (hasErrors || !sendMessageState.data.names || !sendMessageState.data.ticFrom) {
            return;
        }

        //variables to test
        let hasNoLeadingSpaceInData = true;
        let hasEnoughCharacters = true;

        //Checks if subject and body do not have a starting space and that they values exist
        if (!regexTester(spaceRegex, sendMessageState.data.subject)) {
            handleNestedChange('starts_with_space_or_undefined_subject', 'errorHandling', 'subject');
            hasNoLeadingSpaceInData = false;
        }
        if (!regexTester(spaceRegex, currentMessage)) {
            handleNestedChange('starts_with_space_or_undefined_message', 'errorHandling', 'msgText');
        }
        if (isInvalidFileError) {
            return;
        }

        if (!hasNoLeadingSpaceInData) {
            return;
        }

        //Checks if ticket from, ticket to, subject and body/msgText do not have a starting space and that they
        //are within the limits set

        if (!regexTester(subjectLengthRegex, sendMessageState.data.subject)) {
            handleNestedChange('character_count_subject', 'errorHandling', 'subject');
            hasEnoughCharacters = false;
        }
        if (!regexTester(minLengthRegex, currentMessage)) {
            handleNestedChange('character_count_message', 'errorHandling', 'msgText');
            hasEnoughCharacters = false;
        }

        if (!hasEnoughCharacters) {
            return;
        }

        handleNestedChange(true, 'data', 'sendComment');
    };

    // Insert selected item into the editor
    const insertSelectedItem = item => {
        const quill = quillRef.current.getEditor();
        if (!quill.getSelection()) quill.focus();
        insertSuggestion(editContent);
        if (selectedSubject === undefined || selectedSubject === '') {
            setSelectedSubject(item?.templateSubject);
            sendMessageState.data.subject = item?.templateSubject;
        }
        setShowSuggestions(false);
    };

    const filterItems = text => {
        const activeTemplates = templateList.data?.filter(template => template.enableAsActive);
        setLastWordLength(text?.length + 1);
        if (text?.trim() !== '' && !isNaN(text)) {
            const num = Number(text);
            setFilteredItems(activeTemplates?.slice(num - 1, num));
        } else {
            setFilteredItems(
                activeTemplates?.filter(template =>
                    template.templateName.toLowerCase()?.includes(text?.replace(/&amp;/g, '&')?.trim().toLowerCase()),
                ),
            );
        }
    };

    const handleKeyDown = e => {
        if (!showSuggestions || filteredItems.length === 0) return;
        if (e.key === ARROW_DOWN) {
            e.preventDefault();
            setCursorIndex(prev => (prev + 1) % filteredItems.length);
        } else if (e.key === ARROW_UP) {
            e.preventDefault();
            setCursorIndex(prev => (prev === 0 ? filteredItems.length - 1 : prev - 1));
        } else if (e.key === ENTER) {
            e.preventDefault();
            setCursorIndex(cursorIndex);
            setTemplateObj(filteredItems[cursorIndex]);
            setEditContent(filteredItems[cursorIndex]?.templateContent);
            setShowEditContentView(true);
            setShowSuggestions(false);
        } else if (e.key === ESCAPE) {
            setShowSuggestions(false);
        }
    };

    const handleChange = (content, delta, source, editor) => {
        const text = editor.getText();
        const cursorPos = editor.getSelection()?.index ?? 0;
        const uptoCursor = text.slice(0, cursorPos);

        // Match hashtag with spaces: starts with # and continues until Enter or a blank line
        const match = uptoCursor.match(/#([^\n]*)$/);

        if (match) {
            filterItems(match[1].trim());
        } else {
            setShowSuggestions(false);
        }

        setCurrentMessage(content);
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection();

        if (!range) return;

        const cursorBounds = quill.getBounds(range.index);
        setDropdownPos({
            top: cursorBounds.top + cursorBounds.height,
            left: cursorBounds.left,
        });
    };

    const insertSuggestion = text => {
        const quill = quillRef.current.getEditor();
    
        if (!quill.getSelection()) {
            quill.focus();
        }
        const range = quill.getSelection();
        if (range) {
            const cursorPos = range.index;
            const textBeforeCursor = quill.getText(0, cursorPos);
            const textAfterCursor = quill.getText(cursorPos);
            const match = textBeforeCursor.trimEnd().match(/#\S*$/);
    
            if (match) {
                const updatedText = `${textBeforeCursor.slice(0, match.index)}${text}${textAfterCursor}`;
                quill.setText(updatedText);
                quill.setSelection(match.index + text.length);
                setShowSuggestions(false);
            }
        }
    };

    const onAcceptedContent = () => {
        insertSelectedItem(templateObj);
        setShowEditContentView(false);
    };

    const onDeclineContent = () => {
        setShowEditContentView(false);
        setTemplateObj('');
        setEditContent('');
    };

    const onItemSelected = item => {
        setCursorIndex(cursorIndex);
        setTemplateObj(item);
        setEditContent(item?.templateContent);
        setShowEditContentView(true);
        setShowSuggestions(false);
    };

    const goBack = () => {
        window.history.back();
    };

    // Handling template text area button (right and close) by keyboard's enter and delete button
    const handleOnchangeTemplateTextArea = event => {
        const { key } = event;
        if (key === ENTER) {
            onAcceptedContent();
        }
        if ([BACKSPACE, DELETE].includes(key)) {
            onDeclineContent();
        }
    };

    const RenderPdfFile = () => (
        <div style={{ display: 'flex', flexDirection: 'column', paddingTop: '1%' }}>
            {uploadedFiles?.length > 0 &&
                uploadedFiles?.map((file, idx) => {
                    if (file) {
                        const fileUrl = URL.createObjectURL(file);
                        return (
                            <div key={idx} style={{ display: 'flex', columnGap: '5%' }}>
                                <div className="grid-col-8" style={{ overflow: 'hidden', wordBreak: 'break-all' }}>
                                    <a href={fileUrl} rel="noopener noreferrer" target="_blank">
                                        {file.name} ({(file.size / (1024 * 1024)).toFixed(2)}MB)
                                    </a>
                                </div>
                                <div className="grid-col-2" onClick={() => onDeleteFileHandler(idx)}>
                                    <span className={trashIconClass}>
                                        <FontAwesomeIcon icon="trash-alt" size="1x" />
                                    </span>
                                </div>
                            </div>
                        );
                    }
                    return null;
                })}
        </div>
    );

    return (
        <div id="ticket-send-message-form">
            <div className="header-styled">
                <h3 className="large-heading pl-5" data-automation-id="h3_heading_send_message_page">
                    <FormattedMessage id="header.ticketmgmt.send_message.page_header" />
                </h3>
            </div>
            <Form id="send-message-form">
                <ConditionalRendering
                    isTrue={!activeSpinnersQty && (!sendMessageState.data.names || !sendMessageState.data.ticFrom)}
                >
                    <div className="grid-row my-4 send-message-labels">
                        <ErrorMessage
                            isVisible={true}
                            messageText={'validation.ticketmgment.send_message.unable_to_retrieve_data'}
                            errorClasses={['ticket-mgment-error', 'ml-5']}
                            errorWrapperClasses={['mx-5', 'pl-5']}
                            id="send_message_missing_data"
                        />
                    </div>
                </ConditionalRendering>

                <div className="grid-row my-4 send-message-labels">
                    <div
                        className="grid-col-2 text-right ticket-send-message-tfs"
                        data-automation-id="lbl_send_message_to"
                    >
                        <FormattedMessage id="form.ticketmgment.send_message.to" />:
                    </div>
                    {(sendMessageState.data.createdForName || sendMessageState.data.createFor) && (
                        <div className="grid-col-10" data-automation-id="content_send_message_to">
                            {' '}
                            <FormattedMessage
                                id="form.ticketmgment.send_message.non_inter_text"
                                values={{
                                    text: sendMessageState.data.createdForName || sendMessageState.data.createFor,
                                }}
                            />{' '}
                        </div>
                    )}
                </div>
                <div className="grid-row my-4 send-message-labels">
                    <div
                        className="grid-col-2 text-right ticket-send-message-tfs"
                        data-automation-id="lbl_send_message_from"
                    >
                        <FormattedMessage id="form.ticketmgment.send_message.from" />:
                    </div>
                    <div className="grid-col-10" data-automation-id="content_send_message_from">
                        {' '}
                        <FormattedMessage
                            id="form.ticketmgment.send_message.non_inter_text"
                            values={{ text: exchangeName }}
                        />{' '}
                    </div>
                </div>
                {sendMessageState.errorHandling.subject && (
                    <div className="grid-row my-4 send-message-labels">
                        <ErrorMessage
                            isVisible={true}
                            messageText={`validation.ticketmgment.send_message.${sendMessageState.errorHandling.subject}`}
                            errorClasses={['ticket-mgment-error']}
                            errorWrapperClasses={['grid-offset-1', 'pl-3']}
                            id="send_message_subject"
                        />
                    </div>
                )}
                <div className="grid-row my-4 send-message-labels">
                    <div
                        className="grid-col-2 text-right ticket-send-message-tfs"
                        data-automation-id="lbl_send_message_subject"
                    >
                        <FormattedMessage id="form.ticketmgment.send_message.subject" />:
                    </div>
                    <div className="grid-col-7">
                        <Form.Control
                            value={sendMessageState.data.subject}
                            size="lg"
                            type="text"
                            aria-label="Subject line for message"
                            name="subject"
                            onChange={e => handleNestedChange(e.target.value, 'data', 'subject')}
                            data-automation-id="txt_box_send_message_subject"
                        />
                    </div>
                </div>
                <div className="grid-row my-4 send-message-labels">
                    <div className="grid-col-2 text-right ticket-send-message-tfs"></div>
                    <div className="grid-col-7">
                        {isInvalidFileError ? (
                            <span className="create-ticket error--msg">
                                &nbsp;
                                <FormattedMessage id={'validation.ticketmgment.send_message.only_pdf_under_25_mb'} />
                            </span>
                        ) : null}
                        <RenderPdfFile />
                        <input
                            type="file"
                            name="file"
                            id="uploadFileId"
                            accept=".pdf"
                            onChange={onFileUploadHandler}
                            onClick={event => (event.target.value = '')}
                            style={{ display: 'none' }}
                            multiple
                            disabled={isProcessing}
                        />
                        <label htmlFor="uploadFileId" className={uploadFileClass}>
                            <FormattedMessage id="button.ticketmgment.send_message.attach_a_pdf" />
                        </label>
                        <ProgressBarContainer />
                    </div>
                </div>
                {sendMessageState.errorHandling.msgText && (
                    <div className="grid-row mx-4 send-message-labels">
                        <ErrorMessage
                            isVisible={true}
                            messageText={`validation.ticketmgment.send_message.${sendMessageState.errorHandling.msgText}`}
                            errorClasses={['ticket-mgment-error']}
                            id="send_message_body-text"
                        />
                    </div>
                )}
                <div
                    className="position-relative"
                    id="quill-container"
                    data-automation-id="txa_editable_quill_send_message"
                >
                    <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        onKeyPress={handleKeyDown}
                        onChange={handleChange}
                        value={currentMessage}
                        modules={modulesForSendMessage}
                        onKeyDown={handleKeyDown}
                    />
                    <ConditionalRendering isTrue={showSuggestions}>
                        <div
                            style={{
                                position: 'absolute',
                                top: dropdownPos.top + 40, // Adjust this based on padding/margins
                                left: dropdownPos.left,
                                backgroundColor: '#fff',
                                border: '1px solid #ccc',
                                zIndex: 1000,
                                borderRadius: '4px',
                                padding: '4px',
                            }}
                        >
                            <div className="send-message-suggestion-main">
                                {filteredItems.map((item, index) => (
                                    <div
                                        key={item}
                                        className={index == cursorIndex ? 'item-style-active' : 'item-style'}
                                        onClick={() => onItemSelected(item)}
                                        onMouseEnter={() => setCursorIndex(index)}
                                        data-testid={`send_message_suggestion_item_${index}`}
                                    >
                                        {index +
                                            1 +
                                            '. ' +
                                            item?.templateName +
                                            ' - ' +
                                            item.templateContent.substring(0, 20) +
                                            '..'}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={showEditContentView}>
                        <div
                            style={{
                                position: 'absolute',
                                width: '700px',
                                left: 14,
                                bottom: 5,
                                backgroundColor: '#fff',
                                zIndex: 1000,
                                padding: '4px',
                            }}
                        >
                            <div className="position-relative">
                                <textarea
                                    type="text"
                                    className="template-form__input"
                                    data-testid="send_message_editable_template_content"
                                    name="templateContent"
                                    ref={inputTextAreaTemplateRef}
                                    rows={5}
                                    value={editContent}
                                    onChange={e => setEditContent(e.target.value)}
                                    onKeyDown={handleOnchangeTemplateTextArea}
                                    style={{ width: '100%', paddingRight: '50px' }} // Add space for buttons
                                />
                                <div
                                    className="feedback-buttons"
                                    style={{
                                        position: 'absolute',
                                        bottom: '8px',
                                        right: '8px',
                                        display: 'flex',
                                        gap: '8px',
                                    }}
                                >
                                    <button
                                        className="feedback-btn check"
                                        type="button"
                                        onClick={() => onAcceptedContent()}
                                        data-testid="btn_send_message_verify_content_yes"
                                    >
                                        ✔
                                    </button>
                                    <button
                                        className="feedback-btn close"
                                        type="button"
                                        onClick={() => onDeclineContent()}
                                        data-testid="btn_send_message_verify_content_cancel"
                                    >
                                        ✖
                                    </button>
                                </div>
                            </div>
                        </div>
                    </ConditionalRendering>
                </div>

                <div id="send-message-buttons" className="mx-3 my-4">
                    <button
                        className="usa-button gi-button gi-button__outline pull-left"
                        type="button"
                        onClick={() => goBack()}
                        data-automation-id="btn_send_message_go_back"
                    >
                        <FormattedMessage id="button.ticketmgment.send_message.cancel" />
                    </button>
                    <button
                        className="usa-button gi-button gi-button__primary pull-right"
                        type="button"
                        onClick={() => verificationForSendMessageForm()}
                        data-automation-id="btn_send_message_submit"
                    >
                        <FormattedMessage id="button.ticketmgment.send_message.send_message" />
                    </button>
                </div>
            </Form>
        </div>
    );
};

export default TicketSendMessageForm;